import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import styles from "./IntroductionTemplate.module.scss";
import classNames from "classnames/bind";
import { useAppDispatch } from "~/redux/hooks";
import FormInput from "~/components/common/form/formInput/FormInput";
import { IntroductionTemplateFormData } from "~/utils/interface/estimateSetting";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import FormInpuEditor from "~/components/common/form/formInpuEditor/FormInpuEditor";
import OutlinedButton from "~/components/common/outlined-button/OutlinedButton";
import { handleCreateIntroductionTemplate } from "~/thunks/estimate-setting/estimateSettingThunk";
import { estimateSettingActions } from "~/thunks/estimate-setting/estimateSettingSlice";

dayjs.extend(relativeTime);
const cx = classNames.bind(styles);

export interface IntroductionTemplateProps {
    templateName?: string;
    templateType?: string;
    location?: string;
    onCancel?: () => void;
}

const IntroductionTemplate = (props: IntroductionTemplateProps) => {
    const { templateName: nameFromParent, templateType, onCancel } = props;
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        templateName: "",
        templateDescription: "<p>Hi {{CUSTOMER_FIRST_NAME}},</p><p>We recognize that safeguarding the people and possessions that hold the most value to you is of utmost importance. Your roof serves as the primary shield against the forces of nature, shielding your home from the elements and ensuring that everything inside remains safe and dry.</p><p>&nbsp;</p><p>Thank you for the opportunity to quote on the repairs and improvements to your home. Please find your estimate below along with upgrade options for potential improvements to your project, if applicable.&nbsp;</p><p>&nbsp;</p><p>The following estimate is for:</p><p>&nbsp;</p><p>1.&nbsp;&nbsp;Removal and disposal of old materials</p><p>2.&nbsp;&nbsp;Supply and installation of new materials</p><p>3.&nbsp;&nbsp;Clean up of entire work area (all nails and other materials)</p><p>&nbsp;&nbsp;</p><p>We don't want you to be personally liable should a worker happen to get injured therefore, maintain a high safety program for all employees and crews. We carry our own liability insurance.&nbsp;</p><p>Once the job is complete, we will perform a thorough inspection of your project to make sure we did everything correctly and up to our strict standards and the site is spotless.</p><p>&nbsp;</p><p>If you have any questions, please give me a call. We always want to provide the best value to our clients. If we are outside your budget, please let me know and we will do our best to work within that.</p><p>&nbsp;</p><p>Kind regards,</p><p>{{SALESPERSON_NAME}}</p><p>{{SALESPERSON_EMAIL}}</p><p>{{SALESPERSON_PHONE}}</p>",
    });

    useEffect(() => {
        if (nameFromParent != null && nameFromParent !== "") {
            setFormData((prev) => ({ ...prev, templateName: nameFromParent }));
        }
    }, [nameFromParent]);

    const handleUpdateFormData = (update: Partial<IntroductionTemplateFormData>) => {
        const data: IntroductionTemplateFormData = { ...formData, ...update };
        setFormData({
            templateName: data.templateName ?? "",
            templateDescription: data.templateDescription ?? "",
        });
    };

    const handleAddIntroduction = () => {
        setLoading(true);
        dispatch(
            handleCreateIntroductionTemplate({
                templateName: formData.templateName ?? "",
                description: formData.templateDescription ?? "",
                type: templateType ?? "",
                content: formData.templateDescription ?? "",
            })
        )
            .then(() => {
                dispatch(estimateSettingActions.changeNeedRefreshDataEstimateTemplates(true));
                onCancel?.();
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return (
        <div className={cx('introduction-template-container')}>
            <div className={cx("template-name-block")}>
                <FormInput
                    name="Template Name"
                    text="Template Name"
                    isViewMode={false}
                    placeholder="Template Name"
                    inputkey="templateName"
                    value={formData.templateName ?? ""}
                    height="56px"
                    onChange={(e) => handleUpdateFormData({ templateName: e.target.value })}
                />
            </div>

            <div className={cx("template-info")}>
                <h3 className={cx("template-info-title")}>Introduction</h3>
                <div className={cx("button-container")}>
                    <OutlinedButton
                        title="Cancel"
                        onClick={() => onCancel?.()}
                        className={cx("cancel-button")}
                    />

                    <PrimaryButton
                        title="Add Introduction"
                        onClick={handleAddIntroduction}
                        className={cx("add-introduction-button")}
                        disabled={loading}
                    />
                </div>
            </div>

            <div className={cx("template-description-block")}>
                <FormInpuEditor
                    name={"templateDescription"}
                    text={""}
                    placeholder={""}
                    inputkey={"templateDescription"}
                    value={formData?.templateDescription}
                    isViewMode={false}
                    errorMessage={""}
                    height="100%"
                    onChange={(value) => {
                        handleUpdateFormData({ templateDescription: value });
                    }}
                />
            </div>
        </div>
    );
};

export default IntroductionTemplate;